import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Platform } from 'react-native';
import { getAccessibleHomes } from '../lib/homeMembership';

export interface Conversation {
  id: string;
  homeowner_id: string;
  type: 'honeybot' | 'home_manager';
  channel: 'in_app' | 'sms';
  home_manager_id: string | null;
  home_id: string | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  homeowner_unread_count: number;
  manager_unread_count: number;
  thread_id: string | null;
  created_at: string;
  home_manager?: {
    id: string;
    full_name: string;
    profile_photo_url: string | null;
  };
  home?: {
    id: string;
    address: string;
    city: string;
    state: string;
  };
}

export function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchConversations();

    // Only set up realtime on web platform
    if (Platform.OS === 'web') {
      const conversationsChannel = supabase
        .channel('conversations-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'conversations'
          },
          (payload) => {
            console.log('Conversation change received:', payload);
            
            if (payload.eventType === 'INSERT') {
              fetchConversations();
            } else if (payload.eventType === 'UPDATE') {
              // Recalculate unread count when conversation is updated
              const updateUnreadCount = async (conversationId: string) => {
                const { count } = await supabase
                  .from('conversation_messages')
                  .select('*', { count: 'exact', head: true })
                  .eq('conversation_id', conversationId)
                  .in('sender_type', ['manager', 'ai'])
                  .is('read_by_homeowner_at', null);

                setConversations(prev => 
                  prev.map(conv => 
                    conv.id === conversationId 
                      ? { ...conv, ...payload.new, homeowner_unread_count: count || 0 }
                      : conv
                  )
                );
              };
              updateUnreadCount(payload.new.id);
            } else if (payload.eventType === 'DELETE') {
              setConversations(prev => 
                prev.filter(conv => conv.id !== payload.old.id)
              );
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'conversation_messages'
          },
          async (payload) => {
            console.log('Message change received:', payload);
            
            // Recalculate unread count for the affected conversation
            const conversationId = payload.new?.conversation_id || payload.old?.conversation_id;
            if (conversationId) {
              const { count } = await supabase
                .from('conversation_messages')
                .select('*', { count: 'exact', head: true })
                .eq('conversation_id', conversationId)
                .in('sender_type', ['manager', 'ai'])
                .is('read_by_homeowner_at', null);

              setConversations(prev => 
                prev.map(conv => 
                  conv.id === conversationId 
                    ? { ...conv, homeowner_unread_count: count || 0 }
                    : conv
                )
              );
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(conversationsChannel);
      };
    }
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Only show in-app conversations in the in-app messaging UI. SMS-channel
      // HoneyBot conversations live in the same table but are surfaced
      // through the admin/messaging tools, not the homeowner app.
      const { data, error: fetchError } = await supabase
        .from('conversations')
        .select(`
          *,
          home_manager:profiles!conversations_home_manager_id_fkey (
            id,
            full_name,
            profile_photo_url
          ),
          home:homes!conversations_home_id_fkey (
            id,
            address,
            city,
            state
          )
        `)
        .eq('homeowner_id', user.id)
        .eq('channel', 'in_app')
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (fetchError) throw fetchError;

      let conversations = data || [];

      // Ensure an in-app HoneyBot conversation always exists.
      // This is idempotent: if a row already exists for (user, honeybot, in_app)
      // we either find it in the fetched list above, or recover from a unique
      // constraint conflict by re-fetching it.
      const hasHoneyBot = conversations.some(
        c => c.type === 'honeybot' && (c.channel ?? 'in_app') === 'in_app'
      );
      if (!hasHoneyBot) {
        const { data: threadData } = await supabase
          .from('threads')
          .select('assistant_thread_id')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const conversationSelect = `
          *,
          home_manager:profiles!conversations_home_manager_id_fkey (
            id,
            full_name,
            profile_photo_url
          ),
          home:homes!conversations_home_id_fkey (
            id,
            address,
            city,
            state
          )
        `;

        const { data: newConv, error: createError } = await supabase
          .from('conversations')
          .insert({
            homeowner_id: user.id,
            type: 'honeybot',
            channel: 'in_app',
            home_manager_id: null,
            home_id: null,
            thread_id: threadData?.assistant_thread_id || null,
          })
          .select(conversationSelect)
          .single();

        if (!createError && newConv) {
          conversations = [newConv, ...conversations];
        } else if (createError) {
          // Likely the channel-scoped unique index fired because the row
          // was created concurrently (e.g. by the auto_create_honeybot
          // trigger or a parallel client request). Recover by fetching it.
          const isUniqueViolation =
            (createError as any).code === '23505' ||
            (createError.message || '').includes('idx_conversations_unique_honeybot_per_user');

          if (isUniqueViolation) {
            const { data: existing } = await supabase
              .from('conversations')
              .select(conversationSelect)
              .eq('homeowner_id', user.id)
              .eq('type', 'honeybot')
              .eq('channel', 'in_app')
              .maybeSingle();

            if (existing) {
              conversations = [existing, ...conversations];
            }
          } else {
            console.error('Error creating HoneyBot conversation:', createError);
          }
        }
      }

      const accessibleHomes = await getAccessibleHomes(user.id);
      for (const home of accessibleHomes) {
        const hasHomeConversation = conversations.some(
          (conversation) => conversation.type === 'home_manager' && conversation.home_id === home.id
        );
        if (hasHomeConversation) continue;

        let homeManagerId: string | null = null;
        if (home.home_manager && home.home_manager !== 'Unassigned') {
          const nameParts = home.home_manager.split(' ').filter(Boolean);
          const search = nameParts.length >= 2
            ? `full_name.ilike.%${nameParts[0]}%,full_name.ilike.%${nameParts[1]}%`
            : `full_name.ilike.%${home.home_manager}%`;

          const { data: manager } = await supabase
            .from('profiles')
            .select('id')
            .eq('is_system_manager', true)
            .or(search)
            .limit(1)
            .single();

          homeManagerId = manager?.id || null;
        }

        const { data: newConv, error: createError } = await supabase
          .from('conversations')
          .insert({
            homeowner_id: user.id,
            type: 'home_manager',
            home_manager_id: homeManagerId,
            home_id: home.id,
          })
          .select(`
            *,
            home_manager:profiles!conversations_home_manager_id_fkey (
              id,
              full_name,
              profile_photo_url
            ),
            home:homes!conversations_home_id_fkey (
              id,
              address,
              city,
              state
            )
          `)
          .single();

        if (!createError && newConv) {
          conversations = [...conversations, newConv];
        }
      }

      // Calculate actual unread counts from messages
      const conversationsWithUnreadCounts = await Promise.all(
        conversations.map(async (conv) => {
          // Count unread messages for homeowner (messages from manager or AI that haven't been read)
          const { count, error: countError } = await supabase
            .from('conversation_messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .in('sender_type', ['manager', 'ai'])
            .is('read_by_homeowner_at', null);

          if (countError) {
            console.error('Error counting unread messages:', countError);
            return conv; // Return original conversation if count fails
          }

          return {
            ...conv,
            homeowner_unread_count: count || 0
          };
        })
      );

      // Sort conversations: HoneyBot first, then by last_message_at
      conversationsWithUnreadCounts.sort((a, b) => {
        if (a.type === 'honeybot' && b.type !== 'honeybot') return -1;
        if (a.type !== 'honeybot' && b.type === 'honeybot') return 1;
        const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return bTime - aTime;
      });

      setConversations(conversationsWithUnreadCounts);
      setError(null);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    fetchConversations();
  };

  return { conversations, loading, error, refetch };
}
